import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";

import { useAuth } from "@/contexts/auth/useAuth";
import { useApi } from "@/hooks";

import {
  getApplicationServerKey,
  getPushSubscription,
  getRegistration,
  subscribetoPushNotifications,
  supportsPushNotifications,
} from "../utils";

import { usePushSubscription } from "./usePushSubscription";

function useRegisterPushNotifications() {
  const api = useApi();
  const { isLoggedIn } = useAuth();

  const { data, isFetched } = usePushSubscription();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!supportsPushNotifications) {
        throw new Error(
          "Push Notifications are not supported in this browser.",
        );
      }

      const registration = await getRegistration();
      const existingSubscription = await getPushSubscription(registration);

      if (existingSubscription.subscription) {
        const currentServerKey = new Uint8Array(
          existingSubscription.subscription.options.applicationServerKey!,
        ).toString();
        const subscriptionServerKey = getApplicationServerKey().toString();

        const currentEndpoint = existingSubscription.subscription.endpoint;
        const subscriptionEndpoint = data?.endpoint;

        if (
          currentServerKey === subscriptionServerKey &&
          currentEndpoint === subscriptionEndpoint
        ) {
          return existingSubscription.subscription;
        } else {
          await existingSubscription.subscription.unsubscribe();
        }
      }

      const newSubscription = await subscribetoPushNotifications(registration);

      await api.post("/api/v1/push/subscription", {
        subscription: newSubscription,
        data: {
          alerts: {
            follow: true,
            favourite: true,
            reblog: true,
            mention: true,
            poll: true,
          },
        },
      });

      return newSubscription;
    },
  });

  useEffect(() => {
    if (isLoggedIn && isFetched) {
      mutation.mutate();
    }
  }, [isFetched, isLoggedIn]);
}

export { useRegisterPushNotifications };
